// src/utils/arcaErrorTranslator.js
// Traductor de errores de ARCA / TusFacturas a mensajes user-friendly en español.
//
// Limpia cualquier referencia al intermediario (TusFacturas, hola@tusfacturas.app,
// "Si necesitas ayuda, contactanos en", "Cod. Error #66611XX", etc.) y devuelve
// título, mensaje user-friendly, acción sugerida y un campo tecnico con el
// detalle crudo por si soporte necesita ver el original.
//
// Uso:
//   import { traducirErrorARCA } from '../../utils/arcaErrorTranslator';
//   const t = traducirErrorARCA(err.response?.data?.error);
//   // t = { code, title, mensaje, accion, tecnico, matched }

const STRIP_PATTERNS = [
    // ───── TusFacturas wrappers ─────
    /^TusFacturas:\s*/i,
    /^TusFacturas\s+Service:\s*/i,
    /^ARCA Factura electr[oó]nica,?\s*informa el siguiente error:\s*/i,
    /\s*-?\s*Si necesitas ayuda,?\s*contactanos en hola@tusfacturas\.app\.?\s*$/i,
    /\s*hola@tusfacturas\.app\.?\s*$/i,
    /^C[óo]d\.?\s*Error:?\s*#?66611[0-9]{2}[A-Z0-9.\-]*\s*-\s*/i,  // TusFacturas Cod. Error
    /\(C[óo]d\.?\s*Error\s*#?66611[0-9]{2}[A-Z0-9.\-]*\)\s*\.?/i,
    /^Error\s+[0-9]+:\s*/i,
    /^WS\s*Error:\s*/i,
    /^SOAP\s*Error:\s*/i,
    /^AFIP\s*Error:\s*/i,
    /^ARCA\s*Error:\s*/i,
    /^Consular\s+m[ée]todo\s+FEParamGetCondicionIvaReceptor\s*\.?/i,
];

/**
 * Quita prefijos / sufijos del intermediario y limpia espacios.
 */
function limpiarMensaje(raw) {
    if (!raw) return '';
    let s = String(raw).trim();
    // Re-aplica los patrones múltiples veces para cubrir chains.
    for (let i = 0; i < 3; i++) {
        const before = s;
        for (const p of STRIP_PATTERNS) {
            s = s.replace(p, '');
        }
        if (s === before) break;
    }
    // Quita comas/puntos sueltos al inicio
    s = s.replace(/^[\s,;\.\-]+/, '').trim();
    return s;
}

// Diccionario de errores conocidos. El orden importa: el primer patrón
// que matchea gana. Cada entrada:
//   test        : regex (case-insensitive)
//   code        : código identificador
//   title       : título corto para el operador
//   mensaje     : descripción user-friendly
//   accion      : qué hacer (opcional)
const ARCA_ERRORS = [
    // ============ RECEPTOR / CONDICION IVA ============
    {
        test: /Condicion IVA receptor no es valido/i,
        code: 'ARCA-10243',
        title: 'El receptor no admite este comprobante',
        mensaje:
            'La condición de IVA del receptor no es válida para la clase de comprobante que se está emitiendo.',
        accion:
            'Si el receptor es EXENTO y Ud. intenta emitir Factura A, verifique que tenga cargada la "Solicitud de Factura A" en su portal de AFIP (Comprobantes en Línea → Administración de Solicitudes). Sin esa solicitud, ARCA sólo admite Factura B para receptores EXENTO. Si el receptor es CONSUMIDOR FINAL, sólo se admite Factura B.'
    },
    {
        test: /Condicion IVA receptor es obligatorio/i,
        code: 'ARCA-10242',
        title: 'Falta la condición de IVA del receptor',
        mensaje:
            'A partir de 01/12/2026 es obligatorio informar la condición frente al IVA del receptor en el comprobante.',
        accion:
            'Actualice el sistema y vuelva a emitir la factura. La condición del receptor debe incluirse en el request (RI, EX, CF o M).'
    },
    {
        test: /(receptor no existe en AFIP|Id Persona no existe)/i,
        code: 'ARCA-10238',
        title: 'El receptor no existe en AFIP',
        mensaje:
            'La CUIT/CUIL del receptor informada no se encuentra registrada en los padrones de AFIP.',
        accion:
            'Verifique que la CUIT sea correcta (11 dígitos, sin guiones). Si es un Tercero reciente, espere unas horas y reintente. Si el error persiste, confirme con el Tercero que su CUIT esté activa en AFIP.'
    },
    {
        test: /(CuitReceptor es obligatorio|CUIT del receptor es obligatorio)/i,
        code: 'ARCA-10240',
        title: 'Falta la CUIT del receptor',
        mensaje: 'ARCA requiere que se informe la CUIT del receptor cuando se emite un comprobante.',
        accion: 'Si Ud. está facturando a un Tercero, asegúrese de haber seleccionado un Tercero con CUIT válida antes de confirmar la factura.'
    },
    {
        test: /(Cuit del receptor formato|CUIT formato inv[áa]lido)/i,
        code: 'ARCA-10237',
        title: 'Formato de CUIT inválido',
        mensaje: 'La CUIT del receptor no tiene el formato esperado por ARCA.',
        accion: 'Verifique que la CUIT tenga 11 dígitos numéricos (sin guiones ni puntos).'
    },

    // ============ PUNTO DE VENTA / TIPO COMPROBANTE ============
    {
        test: /(no se permite realizar este tipo de comprobante|condici[oó]n de IVA configurada en tu punto de venta)/i,
        code: 'ARCA-1016',
        title: 'El punto de venta no admite este comprobante',
        mensaje:
            'El punto de venta desde el cual se emite no está habilitado para la clase de comprobante solicitada.',
        accion:
            'Verifique en AFIP → Administrador de Puntos de Venta que el punto de venta esté habilitado para Factura A, B, C o M según corresponda. Si emite Factura A, también confirme que el emisor sea Responsable Inscripto. Si es un Tercero EXENTO, recuerde que ARCA exige la "Solicitud de Factura A" en el portal del receptor.'
    },
    {
        test: /(Para la condicion de IVA seleccionada no se permite realizar comprobantes A y\/o M)/i,
        code: 'ARCA-10243B',
        title: 'El receptor no admite Factura A ni M',
        mensaje:
            'La condición de IVA del receptor no admite comprobantes tipo A ni M.',
        accion:
            'Emita como Factura B, o coordine con el Tercero para que cargue la "Solicitud de Factura A" en su portal de AFIP si requiere A.'
    },
    {
        test: /(PtoVta.*no pertenece|pto.*venta.*no pertenece|el punto de venta no es v[áa]lido)/i,
        code: 'ARCA-1017',
        title: 'Punto de venta inválido',
        mensaje: 'El punto de venta informado no existe o no pertenece al emisor.',
        accion: 'Confirme con el emisor el código del punto de venta habilitado.'
    },

    // ============ NUMERACIÓN ============
    {
        test: /No se pudo corroborar con ARCA la ultima numeracion emitida/i,
        code: 'ARCA-6661133',
        title: 'Numeración inconsistente con ARCA',
        mensaje:
            'ARCA devolvió una numeración distinta a la esperada para el punto de venta. Esto suele ser una inestabilidad temporal del servicio de facturación.',
        accion:
            'Espere 1-2 minutos y reintente. Si el error persiste, contacte a soporte con el número de código de error para revisión manual.'
    },

    // ============ TRANSACCIONES / LOCKS ============
    {
        test: /existe una transaccion activa que se liberara automaticamente/i,
        code: 'ARCA-6661137',
        title: 'Hay una transacción activa en ARCA',
        mensaje:
            'Ya hay una transacción en curso para este punto de venta. ARCA la libera automáticamente en unos minutos.',
        accion:
            'Espere 1-2 minutos y reintente. Si el error persiste más de 5 minutos, contacte a soporte.'
    },

    // ============ CAE / RESPUESTA DE ARCA ============
    {
        test: /ARCA No devolvio el CAE asociado/i,
        code: 'ARCA-6661141',
        title: 'ARCA no devolvió el CAE',
        mensaje:
            'ARCA rechazó el comprobante y no devolvió un CAE. La emisión no quedó registrada.',
        accion:
            'Reintente. Si el error se repite, verifique los datos del comprobante (importes, CUIT, condición IVA) y reintente. Si persiste, contacte a soporte con el código S#### del error.'
    },

    // ============ PRODUCTO / DETALLE ============
    {
        test: /El producto no posee una descripcion v[áa]lida/i,
        code: 'ARCA-7001',
        title: 'El producto no tiene descripción',
        mensaje:
            'Uno o más productos del comprobante no tienen una descripción válida para ARCA.',
        accion:
            'Verifique que cada item tenga un nombre/descripción de al menos 1 carácter. Si carga items personalizados, asegúrese de que el campo productName esté completo.'
    },
    {
        test: /excede el maximo permitido de 20 caracteres/i,
        code: 'ARCA-7003',
        title: 'Código de producto demasiado largo',
        mensaje:
            'Uno o más productos tienen un código que excede el máximo permitido (20 caracteres).',
        accion:
            'Revise los SKUs de los productos del comprobante y acórtelos a 20 caracteres como máximo. ARCA/TusFacturas no acepta códigos más largos.'
    },
    {
        test: /(C[óo]digo de producto no es v[áa]lido|producto sin c[óo]digo)/i,
        code: 'ARCA-7002',
        title: 'Producto sin código',
        mensaje: 'Uno o más productos del comprobante no tienen un código identificador.',
        accion: 'Verifique que cada item tenga un ProductCode (SKU) o identificador interno.'
    },

    // ============ IMPORTES / TOTALES ============
    {
        test: /(ImporteTotal.*no coincide|Total.*no coincide|el total.*no cuadra)/i,
        code: 'ARCA-10221',
        title: 'El total no cuadra',
        mensaje:
            'El total informado del comprobante no coincide con la suma de los items y los impuestos.',
        accion:
            'Verifique que subtotal + IVA = total. Si hay descuentos aplicados, asegúrese de que el backend los haya computado antes de enviar a ARCA.'
    },
    {
        test: /(Iva discriminado|ImporteIva.*obligatorio)/i,
        code: 'ARCA-10235',
        title: 'Falta IVA discriminado',
        mensaje: 'Para comprobantes Factura A es obligatorio discriminar el IVA.',
        accion: 'Asegúrese de que el cálculo envíe el subtotal sin IVA y el importe de IVA por separado.'
    },

    // ============ FECHAS / PERÍODOS ============
    {
        test: /(Fecha del comprobante.*fuera de rango|CbteFch.*inv[áa]lida)/i,
        code: 'ARCA-10228',
        title: 'Fecha del comprobante inválida',
        mensaje:
            'La fecha del comprobante está fuera del rango permitido por ARCA (5 días corridos antes/después de hoy).',
        accion: 'Verifique la fecha del sistema y la zona horaria del servidor. Si el reloj está atrasado o adelantado, corríjalo.'
    },

    // ============ NOTAS DE CRÉDITO ============
    {
        test: /se debe enviar el detalle de los comprobantes asociados/i,
        code: 'ARCA-200',
        title: 'Falta el comprobante asociado',
        mensaje:
            'Para emitir esta Nota de Crédito, ARCA requiere el detalle de los comprobantes originales asociados.',
        accion:
            'Verifique que el comprobante original referenciado (factura o nota de débito) exista y esté aprobado. El sistema debe enviar los datos completos del comprobante asociado (tipo, punto de venta, número, CUIT del receptor).'
    },
    {
        test: /(El comprobante asociado no existe|CbteAsoc.*no existe)/i,
        code: 'ARCA-200',
        title: 'No se encuentra el comprobante asociado',
        mensaje:
            'Para emitir una Nota de Crédito, debe referenciar un comprobante original que no fue encontrado.',
        accion:
            'Verifique que el número de comprobante original (tipo, punto de venta, número) corresponda a una factura ya emitida y aprobada.'
    },

    // ============ MONOTRIBUTO / RESTRICCIONES DEL EMISOR ============
    {
        test: /(Monotributo no puede emitir|No se permite Factura A para Monotributo)/i,
        code: 'ARCA-10246',
        title: 'El emisor Monotributo no puede emitir Factura A',
        mensaje:
            'Un emisor Monotributo sólo puede emitir Factura C (comprobantes sin discriminación de IVA).',
        accion:
            'Si el comprobante es para un Tercero, cambie el tipo a Factura C. Si necesita Factura A, el Tercero debe ser cargado como un nuevo emisor Responsable Inscripto.'
    },

    // ============ TUSFACTURAS (intermediario) ============
    {
        test: /(Las credencials API son invalidas|credencials API son invalidas|API.*invalida)/i,
        code: 'TF-API-AUTH',
        title: 'Credenciales de API inválidas',
        mensaje:
            'Las credenciales del proveedor de facturación no son válidas. El sistema no puede emitir comprobantes hasta que se corrijan.',
        accion:
            'Contacte al administrador del sistema. Las credenciales del proveedor (apikey / apitoken / usertoken) deben revisarse en el .env y/o en el panel del proveedor.'
    },
    {
        test: /Estaremos realizando tareas de mantenimiento/i,
        code: 'TF-MAINT',
        title: 'Proveedor de facturación en mantenimiento',
        mensaje:
            'El proveedor de facturación está en mantenimiento programado. No se pueden emitir comprobantes hasta que finalice.',
        accion: 'Espere el tiempo indicado por el proveedor y reintente. Si el error persiste más de la hora estimada, contacte a soporte.'
    },
    {
        test: /(No fue posible generar el comprobante debido a errores en los servicios web de facturacion de ARCA)/i,
        code: 'TF-ARCA-CAYO',
        title: 'ARCA no responde',
        mensaje:
            'No se pudo generar el comprobante porque los servicios web de ARCA están teniendo problemas generalizados.',
        accion:
            'Espere unos minutos y reintente. Si el problema persiste más de 30 minutos, el operador de ARCA/ARCA ya está notificado. También puede optar por dejar la venta como "programada" para emitirla más tarde.'
    },

    // ============ AUTENTICACIÓN (WSAA) ============
    {
        test: /ValidacionDeToken|No valido token|TA.*expirado|token.*expirado/i,
        code: 'ARCA-600',
        title: 'La sesión con AFIP expiró',
        mensaje: 'El Ticket de Acceso (TA) generado para invocar a ARCA venció.',
        accion:
            'El backend renueva automáticamente el TA cada 12h. Si el error persiste, contacte a soporte para verificar el certificado AFIP del emisor.'
    },

    // ============ GENÉRICO (CAIDA) ============
    {
        test: /No existe la factura|No se encuentra la factura/i,
        code: 'ARCA-200',
        title: 'No se encuentra la factura',
        mensaje: 'ARCA no encuentra el comprobante solicitado.',
        accion: 'Verifique que el número y punto de venta sean correctos y que la factura haya sido emitida y aprobada.'
    },
];

/**
 * Traduce un mensaje crudo de ARCA (vía TusFacturas o cualquier intermediario)
 * a un objeto con título, mensaje user-friendly y acción sugerida.
 *
 * @param {string} rawMessage
 * @returns {{
 *   code: string|null,
 *   title: string,
 *   mensaje: string,
 *   accion: string|null,
 *   tecnico: string,
 *   matched: boolean
 * }}
 */
export function traducirErrorARCA(rawMessage) {
    const tecnico = limpiarMensaje(rawMessage);
    if (!tecnico) {
        return {
            code: null,
            title: 'Error desconocido',
            mensaje: 'ARCA rechazó la operación sin un mensaje identificable.',
            accion: 'Reintente en unos minutos. Si el error persiste, contacte a soporte con el log del backend.',
            tecnico: '',
            matched: false
        };
    }

    for (const entry of ARCA_ERRORS) {
        if (entry.test.test(tecnico)) {
            return {
                code: entry.code,
                title: entry.title,
                mensaje: entry.mensaje,
                accion: entry.accion || null,
                tecnico,
                matched: true
            };
        }
    }

    // Sin match: devolvemos el mensaje limpio, sin título sugerido.
    return {
        code: null,
        title: 'ARCA rechazó la operación',
        mensaje: tecnico,
        accion:
            'Revise los datos del comprobante y la configuración del emisor en AFIP. Si el error persiste, contacte a soporte.',
        tecnico,
        matched: false
    };
}
